import { createSlice, createSelector } from '@reduxjs/toolkit';
import { setUserPreferencesAsync } from '@/store/slices/UserPreferencesSlice';
import initialState, {
  createBulkLinkModalInitialState,
  createDetailModalInitialState,
} from './initialState';
import getFileManagerItemNameErrorKey from './errorUtils';
import {
  createFileFolder,
  createFileLink,
  deleteFileItems,
  enterFolderFromFilteredResults,
  fetchFileItemDetail,
  fetchFileItemLinkedEntities,
  bulkLinkFileItemEntities,
  fetchFileItems,
  getBulkLinkModalContacts,
  getBulkLinkModalDevelopments,
  getBulkLinkModalProperties,
  getDetailModalContacts,
  getDetailModalDevelopments,
  getDetailModalProperties,
  linkFileItemEntity,
  moveFileItem,
  persistFileManagerViewMode,
  renameFileItem,
  toggleFileItemEntityPublic,
  unlinkFileItemEntity,
  updateFileItemDetails,
} from './services';
import {
  FILE_MANAGER_MATCH_MODE,
  SORT_BY,
  SORT_DIRECTION,
  buildListQueryKey,
  filterUnlinkedEntitySearchResults,
  getEffectiveSearchQuery,
  hasActiveFileManagerFilters,
  LINKED_FIELD_BY_ENTITY_KEY,
  mapViewModeFromApi,
} from './utils';

const BULK_LINK_ENTITY_KEYS = ['properties', 'contacts', 'developments'];
const VALID_VIEW_MODES = ['list', 'grid'];
const VALID_SORT_BY = Object.values(SORT_BY);
const VALID_SORT_DIRECTION = Object.values(SORT_DIRECTION);
const VALID_MATCH_MODE = Object.values(FILE_MANAGER_MATCH_MODE);

function findListItemIndex(state, itemId) {
  return state.items.findIndex((item) => item.id === itemId);
}

function setListItemLinkedEntities(state, itemId, apiLinkedEntities) {
  const index = findListItemIndex(state, itemId);

  if (index === -1) {
    return;
  }

  state.items[index].linkedEntities = apiLinkedEntities ?? [];
}

function setListItemName(state, itemId, name) {
  const index = findListItemIndex(state, itemId);

  if (index === -1) {
    return;
  }

  state.items[index].name = name;
}

function stripNoDataSearchResults(data = []) {
  return data.filter((item) => !item?.noData);
}

function refreshDetailEntitySearchResults(state) {
  const { linkedEntities } = state.detailModal;

  state.detailModal.contacts.data = filterUnlinkedEntitySearchResults(
    stripNoDataSearchResults(state.detailModal.contacts.data),
    linkedEntities.linkedContacts
  );
  state.detailModal.properties.data = filterUnlinkedEntitySearchResults(
    stripNoDataSearchResults(state.detailModal.properties.data),
    linkedEntities.linkedProperties
  );
  state.detailModal.developments.data = filterUnlinkedEntitySearchResults(
    stripNoDataSearchResults(state.detailModal.developments.data),
    linkedEntities.linkedDevelopments
  );
}

function refreshBulkLinkEntitySearchResults(state) {
  const { linkedEntities } = state.bulkLinkModal;

  state.bulkLinkModal.contacts.data = filterUnlinkedEntitySearchResults(
    stripNoDataSearchResults(state.bulkLinkModal.contacts.data),
    linkedEntities.linkedContacts
  );
  state.bulkLinkModal.properties.data = filterUnlinkedEntitySearchResults(
    stripNoDataSearchResults(state.bulkLinkModal.properties.data),
    linkedEntities.linkedProperties
  );
  state.bulkLinkModal.developments.data = filterUnlinkedEntitySearchResults(
    stripNoDataSearchResults(state.bulkLinkModal.developments.data),
    linkedEntities.linkedDevelopments
  );
}

function clearBulkLinkModalSearchResults(bulkLinkModal) {
  BULK_LINK_ENTITY_KEYS.forEach((entityKey) => {
    bulkLinkModal[entityKey].searchRequestId += 1;
    bulkLinkModal[entityKey].data = [];
    bulkLinkModal[entityKey].loading = false;
    bulkLinkModal[entityKey].error = null;
  });
}

function addEntitySearchCases(
  builder,
  { thunk, modalKey, entityKey, pickResults }
) {
  const linkedField = LINKED_FIELD_BY_ENTITY_KEY[entityKey];

  builder
    .addCase(thunk.pending, (state) => {
      state[modalKey][entityKey].searchRequestId += 1;
      state[modalKey][entityKey].loading = true;
      state[modalKey][entityKey].error = null;
    })
    .addCase(thunk.fulfilled, (state, action) => {
      const { requestId, data } = action.payload;

      if (requestId !== state[modalKey][entityKey].searchRequestId) {
        return;
      }

      state[modalKey][entityKey].loading = false;
      state[modalKey][entityKey].data = filterUnlinkedEntitySearchResults(
        pickResults(data),
        state[modalKey].linkedEntities[linkedField]
      );
    })
    .addCase(thunk.rejected, (state, action) => {
      const requestId = action.payload?.requestId;

      if (
        requestId !== undefined &&
        requestId !== state[modalKey][entityKey].searchRequestId
      ) {
        return;
      }

      state[modalKey][entityKey].loading = false;
      state[modalKey][entityKey].error = true;
    });
}

function beginListLoading(state) {
  state.listStatus = 'loading';
  state.items = [];
  state.total = 0;
  state.selectedIds = [];
  state.page = 1;
}

function getEntityIds(entities = []) {
  return entities.map((entity) => entity.id).join(',');
}

function sameUploadDateRange(leftRange, rightRange) {
  const [leftStart, leftEnd] = leftRange ?? [null, null];
  const [rightStart, rightEnd] = rightRange ?? [null, null];

  return (
    (leftStart?.getTime() ?? null) === (rightStart?.getTime() ?? null) &&
    (leftEnd?.getTime() ?? null) === (rightEnd?.getTime() ?? null)
  );
}

function resetFolderNavigation(state) {
  state.currentFolderId = null;
  state.folderTrail = [];
}

function matchesListQueryKey(state, queryKey) {
  if (!queryKey) {
    return true;
  }

  const currentQueryKey = buildListQueryKey(state);

  return Object.keys(queryKey).every(
    (key) => currentQueryKey[key] === queryKey[key]
  );
}

export const FileManagerSlice = createSlice({
  name: 'FileManager',
  initialState,
  reducers: {
    setViewMode(state, action) {
      if (VALID_VIEW_MODES.includes(action.payload)) {
        state.viewMode = action.payload;
        state.viewPreferenceDirty = true;
      }
    },
    toggleSelection(state, action) {
      const id = action.payload;
      const index = state.selectedIds.indexOf(id);

      if (index === -1) {
        state.selectedIds.push(id);
      } else {
        state.selectedIds.splice(index, 1);
      }
    },
    clearSelection(state) {
      state.selectedIds = [];
    },
    setCurrentFolderId(state, action) {
      state.currentFolderId = action.payload ?? null;
      state.selectedIds = [];
    },
    enterFolder(state, action) {
      beginListLoading(state);
      const { id, name } = action.payload;
      state.currentFolderId = id;
      state.selectedIds = [];
      state.folderTrail.push({ id, name });
    },
    navigateToFolder(state, action) {
      beginListLoading(state);
      const folderId = action.payload ?? null;
      state.selectedIds = [];

      if (folderId == null) {
        state.currentFolderId = null;
        state.folderTrail = [];
        return;
      }

      const index = state.folderTrail.findIndex(
        (folder) => folder.id === folderId
      );

      if (index === -1) {
        state.currentFolderId = folderId;
        return;
      }

      state.folderTrail = state.folderTrail.slice(0, index + 1);
      state.currentFolderId = folderId;
    },
    setPage(state, action) {
      const nextPage = Number(action.payload);

      if (!Number.isInteger(nextPage) || nextPage < 1) {
        return;
      }

      if (state.page === nextPage) {
        return;
      }

      // Keep total so the paginator stays mounted while the page loads.
      state.listStatus = 'loading';
      state.items = [];
      state.selectedIds = [];
      state.page = nextPage;
    },
    setSortBy(state, action) {
      if (
        VALID_SORT_BY.includes(action.payload) &&
        state.sortBy !== action.payload
      ) {
        beginListLoading(state);
        state.sortBy = action.payload;
      }
    },
    setSortDirection(state, action) {
      if (
        VALID_SORT_DIRECTION.includes(action.payload) &&
        state.sortDirection !== action.payload
      ) {
        beginListLoading(state);
        state.sortDirection = action.payload;
      }
    },
    setMatchMode(state, action) {
      if (
        VALID_MATCH_MODE.includes(action.payload) &&
        state.matchMode !== action.payload
      ) {
        beginListLoading(state);
        state.matchMode = action.payload;
      }
    },
    setSearchQuery(state, action) {
      const nextQuery = action.payload ?? '';

      if (state.searchQuery !== nextQuery) {
        beginListLoading(state);
        state.searchQuery = nextQuery;

        if (getEffectiveSearchQuery(nextQuery)) {
          resetFolderNavigation(state);
        }
      }
    },
    setUploadedBy(state, action) {
      const uploadedBy = action.payload ?? [];

      if (getEntityIds(state.uploadedBy) === getEntityIds(uploadedBy)) {
        return;
      }

      beginListLoading(state);
      state.uploadedBy = uploadedBy;

      if (uploadedBy.length > 0) {
        resetFolderNavigation(state);
      }
    },
    clearUploadedBy(state) {
      if (state.uploadedBy.length > 0) {
        beginListLoading(state);
      }
      state.uploadedBy = [];
    },
    setUploadDateRange(state, action) {
      const [startDate, endDate] = action.payload ?? [null, null];
      const nextStartDate = startDate ?? null;
      const nextEndDate = endDate ?? null;
      const nextRange = [nextStartDate, nextEndDate];

      if (sameUploadDateRange(state.uploadDateRange, nextRange)) {
        return;
      }

      beginListLoading(state);
      state.uploadDateRange = nextRange;

      if (nextStartDate || nextEndDate) {
        resetFolderNavigation(state);
      }
    },
    clearUploadDateRange(state) {
      const [startDate, endDate] = state.uploadDateRange ?? [null, null];

      if (startDate || endDate) {
        beginListLoading(state);
      }

      state.uploadDateRange = [null, null];
    },
    setLinkedContacts(state, action) {
      const linkedContacts = action.payload ?? [];

      if (getEntityIds(state.linkedContacts) === getEntityIds(linkedContacts)) {
        return;
      }

      beginListLoading(state);
      state.linkedContacts = linkedContacts;

      if (linkedContacts.length > 0) {
        resetFolderNavigation(state);
      }
    },
    clearLinkedContacts(state) {
      if (state.linkedContacts.length > 0) {
        beginListLoading(state);
      }
      state.linkedContacts = [];
    },
    setLinkedProperties(state, action) {
      const linkedProperties = action.payload ?? [];

      if (
        getEntityIds(state.linkedProperties) === getEntityIds(linkedProperties)
      ) {
        return;
      }

      beginListLoading(state);
      state.linkedProperties = linkedProperties;

      if (linkedProperties.length > 0) {
        resetFolderNavigation(state);
      }
    },
    clearLinkedProperties(state) {
      if (state.linkedProperties.length > 0) {
        beginListLoading(state);
      }
      state.linkedProperties = [];
    },
    removeLinkedContact(state, action) {
      if (
        state.linkedContacts.some((contact) => contact.id === action.payload)
      ) {
        beginListLoading(state);
      }

      state.linkedContacts = state.linkedContacts.filter(
        (contact) => contact.id !== action.payload
      );
    },
    removeLinkedProperty(state, action) {
      if (
        state.linkedProperties.some(
          (property) => property.id === action.payload
        )
      ) {
        beginListLoading(state);
      }

      state.linkedProperties = state.linkedProperties.filter(
        (property) => property.id !== action.payload
      );
    },
    removeLinkedDevelopment(state, action) {
      if (
        state.linkedDevelopments.some(
          (development) => development.id === action.payload
        )
      ) {
        beginListLoading(state);
      }

      state.linkedDevelopments = state.linkedDevelopments.filter(
        (development) => development.id !== action.payload
      );
    },
    setLinkedDevelopments(state, action) {
      const linkedDevelopments = action.payload ?? [];

      if (
        getEntityIds(state.linkedDevelopments) ===
        getEntityIds(linkedDevelopments)
      ) {
        return;
      }

      beginListLoading(state);
      state.linkedDevelopments = linkedDevelopments;

      if (linkedDevelopments.length > 0) {
        resetFolderNavigation(state);
      }
    },
    clearLinkedDevelopments(state) {
      if (state.linkedDevelopments.length > 0) {
        beginListLoading(state);
      }
      state.linkedDevelopments = [];
    },
    clearFilters(state) {
      if (hasActiveFileManagerFilters(state)) {
        beginListLoading(state);
      }

      state.searchQuery = '';
      state.uploadedBy = [];
      state.uploadDateRange = [null, null];
      state.linkedContacts = [];
      state.linkedProperties = [];
      state.linkedDevelopments = [];
      state.matchMode = FILE_MANAGER_MATCH_MODE.ANY;
    },
    openCreateFolderModal(state) {
      state.isCreateFolderModalOpen = true;
      state.createFolderStatus = 'idle';
    },
    closeCreateFolderModal(state) {
      state.isCreateFolderModalOpen = false;
      state.createFolderStatus = 'idle';
    },
    resetCreateFolderStatus(state) {
      if (state.createFolderStatus === 'failed') {
        state.createFolderStatus = 'idle';
      }
    },
    openRenameItemModal(state, action) {
      state.renameItem = action.payload;
      state.renameStatus = 'idle';
      state.renameErrorKey = null;
    },
    closeRenameItemModal(state) {
      state.renameItem = null;
      state.renameStatus = 'idle';
      state.renameErrorKey = null;
    },
    openPreviewItemModal(state, action) {
      state.previewItem = action.payload;
    },
    closePreviewItemModal(state) {
      state.previewItem = null;
    },
    resetRenameStatus(state) {
      if (state.renameStatus === 'failed') {
        state.renameStatus = 'idle';
        state.renameErrorKey = null;
      }
    },
    openMoveItemModal(state, action) {
      state.moveItem = action.payload;
      state.moveStatus = 'idle';
    },
    closeMoveItemModal(state) {
      state.moveItem = null;
      state.moveStatus = 'idle';
    },
    resetMoveStatus(state) {
      if (state.moveStatus === 'failed') {
        state.moveStatus = 'idle';
      }
    },
    openDeleteItemsModal(state, action) {
      state.deleteItems = action.payload;
      state.deleteStatus = 'idle';
    },
    closeDeleteItemsModal(state) {
      state.deleteItems = null;
      state.deleteStatus = 'idle';
    },
    openDetailItemModal(state, action) {
      state.detailModal = {
        ...createDetailModalInitialState(),
        itemId: action.payload?.id ?? null,
        itemType: action.payload?.type ?? null,
        status: 'loading',
        linkedEntitiesStatus: 'loading',
      };
    },
    closeDetailItemModal(state) {
      state.detailModal = createDetailModalInitialState();
    },
    retryDetailItemModal(state) {
      if (!state.detailModal.itemId) {
        return;
      }

      state.detailModal.status = 'loading';
      state.detailModal.linkedEntitiesStatus = 'loading';
      state.detailModal.error = null;
    },
    resetDetailUpdateStatus(state) {
      if (state.detailModal.updateItemStatus === 'failed') {
        state.detailModal.updateItemStatus = 'idle';
        state.detailModal.updateItemErrorKey = null;
        state.detailModal.updateItemField = null;
      }
    },
    clearDetailEntityResults(state, action) {
      const { entityKey } = action.payload;

      if (!state.detailModal[entityKey]) {
        return;
      }

      state.detailModal[entityKey].searchRequestId += 1;
      state.detailModal[entityKey].data = [];
      state.detailModal[entityKey].loading = false;
      state.detailModal[entityKey].error = null;
    },
    openBulkLinkModal(state) {
      state.bulkLinkModal = {
        ...createBulkLinkModalInitialState(),
        itemIds: [...state.selectedIds],
      };
    },
    closeBulkLinkModal(state) {
      state.bulkLinkModal = createBulkLinkModalInitialState();
    },
    setBulkLinkMode(state, action) {
      const mode = action.payload === 'unlink' ? 'unlink' : 'link';

      if (state.bulkLinkModal.mode === mode) {
        return;
      }

      state.bulkLinkModal.mode = mode;
      state.bulkLinkModal.linkedEntities = {
        linkedContacts: [],
        linkedProperties: [],
        linkedDevelopments: [],
      };
      clearBulkLinkModalSearchResults(state.bulkLinkModal);
    },
    addBulkLinkEntity(state, action) {
      const { entityKey, item } = action.payload;

      if (!BULK_LINK_ENTITY_KEYS.includes(entityKey) || !item?.id) {
        return;
      }

      const linkedField = LINKED_FIELD_BY_ENTITY_KEY[entityKey];
      const linkedList = state.bulkLinkModal.linkedEntities[linkedField];

      if (linkedList.some((current) => current?.id === item.id)) {
        return;
      }

      const itemWithDefaults =
        entityKey === 'contacts'
          ? item
          : { ...item, showInWeb: item.showInWeb ?? false };

      state.bulkLinkModal.linkedEntities[linkedField] = [
        ...linkedList,
        itemWithDefaults,
      ];
      state.bulkLinkModal[entityKey].data = state.bulkLinkModal[
        entityKey
      ].data.filter((current) => current?.id !== item.id);
      refreshBulkLinkEntitySearchResults(state);
    },
    removeBulkLinkEntity(state, action) {
      const { entityKey, item } = action.payload;

      if (!BULK_LINK_ENTITY_KEYS.includes(entityKey)) {
        return;
      }

      const linkedField = LINKED_FIELD_BY_ENTITY_KEY[entityKey];
      const itemId = item?.id;

      state.bulkLinkModal.linkedEntities[linkedField] =
        state.bulkLinkModal.linkedEntities[linkedField].filter((current) =>
          itemId ? current?.id !== itemId : current !== item
        );
    },
    toggleBulkLinkEntityShowInWeb(state, action) {
      const { entityKey, itemId } = action.payload;

      if (!BULK_LINK_ENTITY_KEYS.includes(entityKey)) {
        return;
      }

      const linkedField = LINKED_FIELD_BY_ENTITY_KEY[entityKey];

      state.bulkLinkModal.linkedEntities[linkedField] =
        state.bulkLinkModal.linkedEntities[linkedField].map((current) =>
          current?.id === itemId
            ? { ...current, showInWeb: !current.showInWeb }
            : current
        );
    },
    clearBulkLinkEntityResults(state, action) {
      const { entityKey } = action.payload;

      if (!state.bulkLinkModal[entityKey]) {
        return;
      }

      state.bulkLinkModal[entityKey].searchRequestId += 1;
      state.bulkLinkModal[entityKey].data = [];
      state.bulkLinkModal[entityKey].loading = false;
      state.bulkLinkModal[entityKey].error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFileItems.pending, (state) => {
        state.listStatus = 'loading';
      })
      .addCase(fetchFileItems.fulfilled, (state, action) => {
        if (state.isOpeningFolderFromFilters) {
          return;
        }

        if (!matchesListQueryKey(state, action.payload.queryKey)) {
          return;
        }

        state.listStatus = 'succeeded';
        state.items = action.payload.items;
        state.total = action.payload.total;
        state.pageSize = action.payload.pageSize;
      })
      .addCase(fetchFileItems.rejected, (state, action) => {
        if (!matchesListQueryKey(state, action.payload?.queryKey)) {
          return;
        }

        state.listStatus = 'failed';
        state.items = [];
        state.total = 0;
      })
      .addCase(enterFolderFromFilteredResults.pending, (state) => {
        beginListLoading(state);
        state.isOpeningFolderFromFilters = true;
        state.searchQuery = '';
        state.uploadedBy = [];
        state.uploadDateRange = [null, null];
        state.linkedContacts = [];
        state.linkedProperties = [];
        state.linkedDevelopments = [];
        state.matchMode = FILE_MANAGER_MATCH_MODE.ANY;
      })
      .addCase(enterFolderFromFilteredResults.fulfilled, (state, action) => {
        beginListLoading(state);
        const { id, folderTrail } = action.payload;
        state.currentFolderId = id;
        state.folderTrail = folderTrail;
        state.isOpeningFolderFromFilters = false;
      })
      .addCase(enterFolderFromFilteredResults.rejected, (state) => {
        state.isOpeningFolderFromFilters = false;
        state.listStatus = 'failed';
        state.items = [];
        state.total = 0;
      })
      .addCase(createFileFolder.pending, (state) => {
        state.createFolderStatus = 'loading';
      })
      .addCase(createFileFolder.fulfilled, (state) => {
        if (state.createFolderStatus !== 'loading') {
          return;
        }

        state.createFolderStatus = 'succeeded';
        state.isCreateFolderModalOpen = false;
      })
      .addCase(createFileFolder.rejected, (state) => {
        state.createFolderStatus = 'failed';
      })
      .addCase(renameFileItem.pending, (state) => {
        state.renameStatus = 'loading';
      })
      .addCase(renameFileItem.fulfilled, (state) => {
        if (state.renameStatus !== 'loading') {
          return;
        }

        state.renameStatus = 'succeeded';
        state.renameItem = null;
      })
      .addCase(renameFileItem.rejected, (state, action) => {
        state.renameStatus = 'failed';
        state.renameErrorKey = getFileManagerItemNameErrorKey(action.payload);
      })
      .addCase(updateFileItemDetails.pending, (state, action) => {
        const { field } = action.meta.arg;

        if (!state.detailModal.itemId) {
          return;
        }

        state.detailModal.updateItemStatus = 'loading';
        state.detailModal.updateItemErrorKey = null;
        state.detailModal.updateItemField = field ?? null;
      })
      .addCase(updateFileItemDetails.fulfilled, (state, action) => {
        const { id, field, response } = action.payload;
        const updatedName = response?.name;
        const updatedDescription = response?.description;

        if (field === 'name' && updatedName != null) {
          setListItemName(state, id, updatedName);
        }

        const isActiveDetailRequest =
          state.detailModal.itemId === id &&
          state.detailModal.updateItemStatus === 'loading';

        if (!isActiveDetailRequest) {
          return;
        }

        state.detailModal.updateItemStatus = 'idle';
        state.detailModal.updateItemErrorKey = null;
        state.detailModal.updateItemField = null;

        if (state.detailModal.item) {
          if (updatedName != null) {
            state.detailModal.item.name = updatedName;
          }

          if (updatedDescription != null) {
            state.detailModal.item.description = updatedDescription;
          }
        }
      })
      .addCase(updateFileItemDetails.rejected, (state, action) => {
        const itemId = action.meta.arg?.id;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.updateItemStatus = 'failed';
        state.detailModal.updateItemErrorKey = getFileManagerItemNameErrorKey(
          action.payload
        );
        state.detailModal.updateItemField =
          action.meta.arg?.field ?? action.payload?.field ?? null;
      })
      .addCase(deleteFileItems.pending, (state) => {
        state.deleteStatus = 'loading';
      })
      .addCase(deleteFileItems.fulfilled, (state, action) => {
        const { ids, emptiedPage } = action.payload;

        state.deleteStatus = 'succeeded';
        state.deleteItems = null;
        state.selectedIds = state.selectedIds.filter((id) => !ids.includes(id));

        if (ids.includes(state.detailModal.itemId)) {
          state.detailModal = createDetailModalInitialState();
        }

        if (emptiedPage) {
          state.listStatus = 'loading';
          state.items = [];
          state.page -= 1;
        }
      })
      .addCase(deleteFileItems.rejected, (state) => {
        state.deleteStatus = 'failed';
      })
      .addCase(moveFileItem.pending, (state) => {
        state.moveStatus = 'loading';
      })
      .addCase(moveFileItem.fulfilled, (state) => {
        if (state.moveStatus !== 'loading') {
          return;
        }

        state.moveStatus = 'succeeded';
        state.moveItem = null;
      })
      .addCase(moveFileItem.rejected, (state) => {
        state.moveStatus = 'failed';
      })
      .addCase(fetchFileItemDetail.pending, (state, action) => {
        const itemId = action.meta.arg;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.status = 'loading';
        state.detailModal.error = null;
      })
      .addCase(fetchFileItemDetail.fulfilled, (state, action) => {
        const { itemId, item, locationTrail } = action.payload;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.status = 'succeeded';
        state.detailModal.item = item;
        state.detailModal.locationTrail = locationTrail;
        state.detailModal.error = null;
      })
      .addCase(fetchFileItemLinkedEntities.pending, (state, action) => {
        const { itemId } = action.meta.arg;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.linkedEntitiesStatus = 'loading';
      })
      .addCase(fetchFileItemLinkedEntities.fulfilled, (state, action) => {
        const { itemId, linkedEntities, apiLinkedEntities } = action.payload;

        setListItemLinkedEntities(state, itemId, apiLinkedEntities);

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.linkedEntitiesStatus = 'succeeded';
        state.detailModal.linkedEntities = linkedEntities;
        refreshDetailEntitySearchResults(state);
      })
      .addCase(fetchFileItemLinkedEntities.rejected, (state, action) => {
        const itemId = action.payload?.itemId ?? action.meta.arg?.itemId;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.linkedEntitiesStatus = 'failed';
      })
      .addCase(fetchFileItemDetail.rejected, (state, action) => {
        const itemId = action.meta.arg;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.status = 'failed';
        state.detailModal.error = true;
      });
    addEntitySearchCases(builder, {
      thunk: getDetailModalContacts,
      modalKey: 'detailModal',
      entityKey: 'contacts',
      pickResults: (data) => data.contacts ?? [],
    });
    addEntitySearchCases(builder, {
      thunk: getDetailModalProperties,
      modalKey: 'detailModal',
      entityKey: 'properties',
      pickResults: (data) => data.properties ?? [],
    });
    addEntitySearchCases(builder, {
      thunk: getDetailModalDevelopments,
      modalKey: 'detailModal',
      entityKey: 'developments',
      pickResults: (data) => data.results ?? [],
    });
    addEntitySearchCases(builder, {
      thunk: getBulkLinkModalContacts,
      modalKey: 'bulkLinkModal',
      entityKey: 'contacts',
      pickResults: (data) => data.contacts ?? [],
    });
    addEntitySearchCases(builder, {
      thunk: getBulkLinkModalProperties,
      modalKey: 'bulkLinkModal',
      entityKey: 'properties',
      pickResults: (data) => data.properties ?? [],
    });
    addEntitySearchCases(builder, {
      thunk: getBulkLinkModalDevelopments,
      modalKey: 'bulkLinkModal',
      entityKey: 'developments',
      pickResults: (data) => data.results ?? [],
    });

    builder
      .addCase(linkFileItemEntity.pending, (state) => {
        state.detailModal.entityActionStatus = 'loading';
      })
      .addCase(linkFileItemEntity.fulfilled, (state, action) => {
        const { itemId, linkedEntities, apiLinkedEntities } = action.payload;

        setListItemLinkedEntities(state, itemId, apiLinkedEntities);

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.entityActionStatus = 'idle';
        state.detailModal.linkedEntities = linkedEntities;
        refreshDetailEntitySearchResults(state);
      })
      .addCase(linkFileItemEntity.rejected, (state, action) => {
        const itemId = action.payload?.itemId ?? state.detailModal.itemId;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        if (action.payload?.reason === 'already_linked') {
          state.detailModal.entityActionStatus = 'idle';
          return;
        }

        state.detailModal.entityActionStatus = 'failed';
      })
      .addCase(unlinkFileItemEntity.pending, (state) => {
        state.detailModal.entityActionStatus = 'loading';
      })
      .addCase(unlinkFileItemEntity.fulfilled, (state, action) => {
        const { itemId, linkedEntities, apiLinkedEntities } = action.payload;

        setListItemLinkedEntities(state, itemId, apiLinkedEntities);

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.entityActionStatus = 'idle';
        state.detailModal.linkedEntities = linkedEntities;
        refreshDetailEntitySearchResults(state);
      })
      .addCase(unlinkFileItemEntity.rejected, (state, action) => {
        const itemId = action.payload?.itemId ?? state.detailModal.itemId;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.entityActionStatus = 'failed';
      })
      .addCase(toggleFileItemEntityPublic.pending, (state) => {
        state.detailModal.entityActionStatus = 'loading';
      })
      .addCase(toggleFileItemEntityPublic.fulfilled, (state, action) => {
        const { itemId, linkedEntities, apiLinkedEntities } = action.payload;

        setListItemLinkedEntities(state, itemId, apiLinkedEntities);

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.entityActionStatus = 'idle';
        state.detailModal.linkedEntities = linkedEntities;
      })
      .addCase(toggleFileItemEntityPublic.rejected, (state, action) => {
        const itemId = action.payload?.itemId ?? state.detailModal.itemId;

        if (state.detailModal.itemId !== itemId) {
          return;
        }

        state.detailModal.entityActionStatus = 'failed';
      })
      .addCase(bulkLinkFileItemEntities.pending, (state) => {
        state.bulkLinkModal.saveStatus = 'loading';
      })
      .addCase(bulkLinkFileItemEntities.fulfilled, (state, action) => {
        if ((action.payload?.total ?? 0) > 0) {
          state.bulkLinkModal = createBulkLinkModalInitialState();
          return;
        }

        state.bulkLinkModal.saveStatus = 'idle';
      })
      .addCase(bulkLinkFileItemEntities.rejected, (state) => {
        state.bulkLinkModal.saveStatus = 'failed';
      })
      .addCase(persistFileManagerViewMode.pending, (state, action) => {
        const viewMode = action.meta.arg;

        if (
          VALID_VIEW_MODES.includes(viewMode) &&
          state.viewMode !== viewMode
        ) {
          state.viewMode = viewMode;
          state.viewPreferenceDirty = true;
        }
      })
      .addCase(setUserPreferencesAsync.fulfilled, (state, action) => {
        if (state.viewPreferenceDirty) {
          return;
        }

        const viewMode = mapViewModeFromApi(
          action.payload?.user_preferences?.file_manager_view
        );

        if (viewMode) {
          state.viewMode = viewMode;
        }
      });
  },
});

export const {
  setViewMode,
  toggleSelection,
  clearSelection,
  setCurrentFolderId,
  enterFolder,
  navigateToFolder,
  setPage,
  setSortBy,
  setSortDirection,
  setMatchMode,
  setSearchQuery,
  setUploadedBy,
  clearUploadedBy,
  setUploadDateRange,
  clearUploadDateRange,
  setLinkedContacts,
  clearLinkedContacts,
  setLinkedProperties,
  clearLinkedProperties,
  removeLinkedContact,
  removeLinkedProperty,
  removeLinkedDevelopment,
  setLinkedDevelopments,
  clearLinkedDevelopments,
  clearFilters,
  openCreateFolderModal,
  closeCreateFolderModal,
  resetCreateFolderStatus,
  openRenameItemModal,
  closeRenameItemModal,
  openPreviewItemModal,
  closePreviewItemModal,
  resetRenameStatus,
  openMoveItemModal,
  closeMoveItemModal,
  resetMoveStatus,
  openDeleteItemsModal,
  closeDeleteItemsModal,
  openDetailItemModal,
  closeDetailItemModal,
  retryDetailItemModal,
  resetDetailUpdateStatus,
  clearDetailEntityResults,
  openBulkLinkModal,
  closeBulkLinkModal,
  setBulkLinkMode,
  addBulkLinkEntity,
  removeBulkLinkEntity,
  toggleBulkLinkEntityShowInWeb,
  clearBulkLinkEntityResults,
} = FileManagerSlice.actions;

export const selectFileManagerViewMode = (state) =>
  state.FileManagerSlice.viewMode;

export const selectFileManagerSelectedIds = (state) =>
  state.FileManagerSlice.selectedIds;

export const selectFileManagerSelectedCount = (state) =>
  state.FileManagerSlice.selectedIds.length;

export const selectFileManagerSelectedItems = createSelector(
  [
    (state) => state.FileManagerSlice.items,
    (state) => state.FileManagerSlice.selectedIds,
  ],
  (items, selectedIds) => items.filter((item) => selectedIds.includes(item.id))
);

export const selectFileManagerHasFolderSelected = createSelector(
  [selectFileManagerSelectedItems],
  (selectedItems) => selectedItems.some((item) => item.type === 'folder')
);

export const selectFileManagerHasFolderOrLinkSelected = createSelector(
  [selectFileManagerSelectedItems],
  (selectedItems) =>
    selectedItems.some((item) => item.type === 'folder' || item.type === 'link')
);

export const selectFileManagerHasSystemItemSelected = createSelector(
  [selectFileManagerSelectedItems],
  (selectedItems) => selectedItems.some((item) => item.isSystem)
);

export const selectFileManagerCurrentFolderId = (state) =>
  state.FileManagerSlice.currentFolderId;

export const selectFileManagerFolderTrail = (state) =>
  state.FileManagerSlice.folderTrail;

export const selectFileManagerItems = (state) => state.FileManagerSlice.items;

export const selectFileManagerTotal = (state) => state.FileManagerSlice.total;

export const selectFileManagerPage = (state) => state.FileManagerSlice.page;

export const selectFileManagerPageSize = (state) =>
  state.FileManagerSlice.pageSize;

export const selectFileManagerListStatus = (state) =>
  state.FileManagerSlice.listStatus;

export const selectFileManagerIsOpeningFolderFromFilters = (state) =>
  state.FileManagerSlice.isOpeningFolderFromFilters;

export const selectFileManagerSortBy = (state) => state.FileManagerSlice.sortBy;

export const selectFileManagerSortDirection = (state) =>
  state.FileManagerSlice.sortDirection;

export const selectFileManagerMatchMode = (state) =>
  state.FileManagerSlice.matchMode;

export const selectFileManagerSearchQuery = (state) =>
  state.FileManagerSlice.searchQuery;

export const selectFileManagerUploadedBy = (state) =>
  state.FileManagerSlice.uploadedBy;

export const selectFileManagerUploadDateRange = (state) =>
  state.FileManagerSlice.uploadDateRange;

export const selectFileManagerLinkedContacts = (state) =>
  state.FileManagerSlice.linkedContacts;

export const selectFileManagerLinkedProperties = (state) =>
  state.FileManagerSlice.linkedProperties;

export const selectFileManagerLinkedDevelopments = (state) =>
  state.FileManagerSlice.linkedDevelopments;

export const selectFileManagerHasActiveFilters = (state) =>
  hasActiveFileManagerFilters(state.FileManagerSlice);

export const selectFileManagerIsCreateFolderModalOpen = (state) =>
  state.FileManagerSlice.isCreateFolderModalOpen;

export const selectFileManagerCreateFolderStatus = (state) =>
  state.FileManagerSlice.createFolderStatus;

export const selectFileManagerRenameItem = (state) =>
  state.FileManagerSlice.renameItem;
export const selectFileManagerPreviewItem = (state) =>
  state.FileManagerSlice.previewItem;

export const selectFileManagerRenameStatus = (state) =>
  state.FileManagerSlice.renameStatus;

export const selectFileManagerRenameErrorKey = (state) =>
  state.FileManagerSlice.renameErrorKey;

export const selectFileManagerMoveItem = (state) =>
  state.FileManagerSlice.moveItem;

export const selectFileManagerMoveStatus = (state) =>
  state.FileManagerSlice.moveStatus;

export const selectFileManagerDeleteItems = (state) =>
  state.FileManagerSlice.deleteItems;

export const selectFileManagerDeleteStatus = (state) =>
  state.FileManagerSlice.deleteStatus;

export const selectFileManagerDetailModal = (state) =>
  state.FileManagerSlice.detailModal;

export const selectFileManagerIsDetailModalOpen = (state) =>
  state.FileManagerSlice.detailModal.itemId != null;

export const selectFileManagerDetailItem = (state) =>
  state.FileManagerSlice.detailModal.item;

export const selectFileManagerDetailStatus = (state) =>
  state.FileManagerSlice.detailModal.status;

export const selectFileManagerDetailLinkedEntities = (state) =>
  state.FileManagerSlice.detailModal.linkedEntities;

export const selectFileManagerDetailLocationTrail = (state) =>
  state.FileManagerSlice.detailModal.locationTrail;

export const selectFileManagerDetailEntitySearchStates = createSelector(
  [(state) => state.FileManagerSlice.detailModal],
  (detailModal) => ({
    properties: detailModal.properties,
    developments: detailModal.developments,
    contacts: detailModal.contacts,
  })
);

export const selectFileManagerDetailLinkedEntitiesStatus = (state) =>
  state.FileManagerSlice.detailModal.linkedEntitiesStatus;

export const selectFileManagerDetailEntityActionStatus = (state) =>
  state.FileManagerSlice.detailModal.entityActionStatus;

export const selectFileManagerDetailUpdateStatus = (state) =>
  state.FileManagerSlice.detailModal.updateItemStatus;

export const selectFileManagerDetailUpdateErrorKey = (state) =>
  state.FileManagerSlice.detailModal.updateItemErrorKey;

export const selectFileManagerDetailUpdateField = (state) =>
  state.FileManagerSlice.detailModal.updateItemField;

export const selectFileManagerBulkLinkModal = (state) =>
  state.FileManagerSlice.bulkLinkModal;

export const selectFileManagerIsBulkLinkModalOpen = (state) =>
  state.FileManagerSlice.bulkLinkModal.itemIds.length > 0;

export const selectFileManagerBulkLinkLinkedEntities = (state) =>
  state.FileManagerSlice.bulkLinkModal.linkedEntities;

export const selectFileManagerBulkLinkMode = (state) =>
  state.FileManagerSlice.bulkLinkModal.mode;

export const selectFileManagerBulkLinkSaveStatus = (state) =>
  state.FileManagerSlice.bulkLinkModal.saveStatus;

export const selectFileManagerBulkLinkEntitySearchStates = createSelector(
  [(state) => state.FileManagerSlice.bulkLinkModal],
  (bulkLinkModal) => ({
    properties: bulkLinkModal.properties,
    developments: bulkLinkModal.developments,
    contacts: bulkLinkModal.contacts,
  })
);

export default FileManagerSlice.reducer;
export {
  fetchFileItems,
  fetchFileItemDetail,
  fetchFileItemLinkedEntities,
  enterFolderFromFilteredResults,
  createFileFolder,
  createFileLink,
  getDetailModalContacts,
  getDetailModalDevelopments,
  getDetailModalProperties,
  linkFileItemEntity,
  persistFileManagerViewMode,
  renameFileItem,
  updateFileItemDetails,
  moveFileItem,
  deleteFileItems,
  toggleFileItemEntityPublic,
  unlinkFileItemEntity,
  bulkLinkFileItemEntities,
  getBulkLinkModalContacts,
  getBulkLinkModalDevelopments,
  getBulkLinkModalProperties,
};
